import classNames from "classnames";
import { BatchStatus } from "react-migration/domains/sites/typings";
import { useTranslation } from "react-migration/lib";

import { Label, Theme, Tooltip } from "@landtechnologies/components";

export type StatusLabelProps = Readonly<{
  status: BatchStatus;
  date?: string;
  error?: string;
}>;

export function StatusLabel({ status, error, date }: StatusLabelProps) {
  let statusText: string;
  let tooltipText: string;
  let className: string;

  const { t } = useTranslation();
  switch (status) {
    case BatchStatus.Cancelled:
      className = classNames("atlas-bg-background-negative-subtle", "atlas-text-content-negative");
      statusText = t("sites.letter_batches.cancelled");
      tooltipText = t("sites.letter_batches.on_date", { date });
      break;
    case BatchStatus.Failed:
      className = classNames("atlas-bg-background-negative-subtle", "atlas-text-content-negative");
      statusText = t("sites.letter_batches.failed");
      tooltipText = error ?? t("sites.letter_batches.unknown_error");
      break;
    case BatchStatus.Created:
      className = classNames("atlas-bg-background-positive-subtle", "atlas-text-content-positive");
      statusText = t("sites.letter_batches.created");
      tooltipText = "";
      break;
    case BatchStatus.Sent:
      className = classNames("atlas-bg-background-positive-subtle", "atlas-text-content-positive");
      statusText = t("sites.letter_batches.sent");
      tooltipText = t("sites.letter_batches.out_for_delivery");
      break;
    case BatchStatus.Pending:
      className = classNames("atlas-bg-background-warning-subtle", "atlas-text-content-warning");
      statusText = t("sites.letter_batches.pending");
      tooltipText = t("sites.letter_batches.prepared_for_delivery");
      break;
    case BatchStatus.Queued:
      className = classNames("atlas-bg-background-warning-subtle", "atlas-text-content-warning");
      statusText = t("sites.letter_batches.queued");
      tooltipText = t("sites.letter_batches.we_are_processing_this_request");
      break;
    default:
      className = "atlas-bg-background-dark";
      statusText = t("sites.letter_batches.unknown");
      tooltipText = t("sites.letter_batches.unknown_status");
  }
  if (!tooltipText) {
    return <Label text={statusText} className={className} />;
  }
  return (
    <div className="atlas-flex atlas-items-center atlas-gap-x-1" data-testid="status-label">
      <Tooltip.Wrapper>
        <Tooltip.Trigger
          className="atlas-pointer-events-auto"
          tabIndex={-1}
          onClick={(event) => event.preventDefault()}
        >
          <Label text={statusText} className={className} />
        </Tooltip.Trigger>
        <Tooltip.Content align="center" side="top" size="sm" theme={Theme.Dark}>
          <span className="atlas-whitespace-normal" data-testid="status-label-tooltip-text">
            {tooltipText}
          </span>
        </Tooltip.Content>
      </Tooltip.Wrapper>
    </div>
  );
}
